import React, { useState, useCallback } from 'react';
import { IconButton, Checkbox, Box, Select, MenuItem, Typography, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import FirstPageIcon from '@material-ui/icons/FirstPage';
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import LastPageIcon from '@material-ui/icons/LastPage';
import { ReviewSortOrder } from '../../../services/types';
import { TablePaginationActionsProps } from '@material-ui/core/TablePagination/TablePaginationActions';

const useStyles = makeStyles(() => ({
  root: {
    flexShrink: 0,
  },

  controls: {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    left: 0,
    top: 0,
    paddingLeft: 4,
    paddingTop: 9,
  },

  pageLabel: {
    display: 'inline-flex',
    color: '#666',
  },

  selectedText: {
    fontSize: 14,
    color: '#666',
    paddingRight: 12,
  },

  pendingButton: {
    backgroundColor: '#333',
    '&:hover': {
      backgroundColor: '#777',
    },
  },
}));

interface IProps {
  order: ReviewSortOrder;
  disableReportedSortOption?: boolean;
  disabledButtons: boolean;
  selectedItemsCount: number;
  itemsTotalCount: number;
  onSelectAllCheckboxClick?: () => void;
  onApproveButtonClick?: () => void;
  onPendingButtonClick?: () => void;
  onBlockButtonClick?: () => void;
  onOrderChange: (order: ReviewSortOrder) => void;
}

const CustomizedTablePaginationActions = (props: IProps) => (paginationProps: TablePaginationActionsProps) =>
  TablePaginationActions(paginationProps, props);

const TablePaginationActions = (paginationProps: TablePaginationActionsProps, customProps: IProps) => {
  const classes = useStyles();

  const { count, page, rowsPerPage, onChangePage } = paginationProps;
  const {
    order: initialOrder,
    disableReportedSortOption,
    disabledButtons,
    selectedItemsCount,
    itemsTotalCount,
    onSelectAllCheckboxClick,
    onApproveButtonClick,
    onPendingButtonClick,
    onBlockButtonClick,
    onOrderChange,
  } = customProps;

  const [order, setOrder] = useState<ReviewSortOrder>(initialOrder);

  const from = page * rowsPerPage + 1;
  const to = (page + 1) * rowsPerPage;

  const handleFirstPageButtonClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    onChangePage(event, 0);
  };

  const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    onChangePage(event, page - 1);
  };

  const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    onChangePage(event, page + 1);
  };

  const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    onChangePage(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  const handleSortChange = useCallback(
    (event: React.ChangeEvent<any>) => {
      const order = event.target.value as ReviewSortOrder;

      setOrder(order);
      onOrderChange(order);
    },
    [onOrderChange],
  );

  const checked = itemsTotalCount === 0 || selectedItemsCount === 0 ? false : selectedItemsCount === itemsTotalCount ? true : undefined;

  return (
    <div className={classes.root}>
      <Box className={classes.controls}>
        {Boolean(onSelectAllCheckboxClick) && (
          <Checkbox color="primary" checked={checked} onChange={onSelectAllCheckboxClick} indeterminate={checked === undefined} />
        )}
        {selectedItemsCount > 0 && <Typography className={classes.selectedText}>{`${selectedItemsCount} Selected`}</Typography>}
        {Boolean(onApproveButtonClick) && (
          <Button
            variant="contained"
            color="primary"
            onClick={onApproveButtonClick}
            disabled={disabledButtons || selectedItemsCount === 0}
            style={{
              textTransform: 'uppercase',
            }}
          >
            Approve
          </Button>
        )}
        {Boolean(onPendingButtonClick) && (
          <Button
            variant="contained"
            color="primary"
            onClick={onPendingButtonClick}
            disabled={disabledButtons || selectedItemsCount === 0}
            style={{
              marginLeft: 8,
              textTransform: 'uppercase',
            }}
            classes={{
              containedPrimary: classes.pendingButton,
            }}
          >
            Pending
          </Button>
        )}
        {Boolean(onBlockButtonClick) && (
          <Button
            variant="contained"
            color="secondary"
            onClick={onBlockButtonClick}
            disabled={disabledButtons || selectedItemsCount === 0}
            style={{
              marginLeft: 8,
              textTransform: 'uppercase',
            }}
          >
            Block
          </Button>
        )}
      </Box>

      <Select value={order} disableUnderline={true} onChange={handleSortChange}>
        <MenuItem value={ReviewSortOrder.Oldest}>Oldest</MenuItem>
        <MenuItem value={ReviewSortOrder.Newest}>Newest</MenuItem>
        <MenuItem value={ReviewSortOrder.Reported} disabled={disableReportedSortOption}>
          Reported
        </MenuItem>
      </Select>

      <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
        <FirstPageIcon />
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        <KeyboardArrowLeft />
      </IconButton>
      <Typography variant="caption" className={classes.pageLabel}>{`${from}-${to > count ? count : to} of ${
        count !== -1 ? count : `more than ${to}`
      }`}</Typography>
      <IconButton onClick={handleNextButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page">
        <KeyboardArrowRight />
      </IconButton>
      <IconButton onClick={handleLastPageButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="last page">
        <LastPageIcon />
      </IconButton>
    </div>
  );
};

export default CustomizedTablePaginationActions;
